import { type ComponentType } from '@angular/cdk/portal';
import { Service, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, type MatSnackBarVerticalPosition } from '@angular/material/snack-bar';

import { ErrorNotification, type ErrorNotificationData } from '@protctc/common/shared/components/error-notification/error-notification';

/** Default notification duration. */
const DEFAULT_DURATION_MS = 2000;

/** Default notification vertical position. */
const DEFAULT_VERTICAL_POSITION: MatSnackBarVerticalPosition = 'top';

/** Default css selector for snackbar container. */
const DEFAULT_SNACKBAR_SELECTOR = 'snackbar';

/** Notification service. */
@Service()
export class NotificationService {
	private readonly snackBar = inject(MatSnackBar);

	/** Config. */
	private readonly config: MatSnackBarConfig;

	public constructor() {
		this.config = new MatSnackBarConfig();
		this.config.panelClass = DEFAULT_SNACKBAR_SELECTOR;
		this.config.verticalPosition = DEFAULT_VERTICAL_POSITION;
		this.config.duration = DEFAULT_DURATION_MS;
	}

	/**
	 * Show notification with custom component.
	 * @param notificationComponent Notification component.
	 * @param config Config.
	 */
	public showNotification<T>(notificationComponent: ComponentType<T>, config = this.config): void {
		this.snackBar.openFromComponent(notificationComponent, config);
	}

	/**
	 * Notify a user with a message.
	 * @param message Human-readable message.
	 */
	public notify(message: string): void {
		this.snackBar.open(message, '', {
			duration: this.config.duration,
			verticalPosition: 'bottom',
			horizontalPosition: 'center',
			panelClass: 'default-snackbar',
		});
	}

	/**
	 * Show an error notification.
	 * @param data Error notification data.
	 */
	public showErrorNotification(data: ErrorNotificationData): void {
		this.snackBar.openFromComponent(ErrorNotification, {
			data,
			duration: this.config.duration,
			horizontalPosition: 'end',
			verticalPosition: 'bottom',
			panelClass: 'snack-bar-error',
		});
	}
}
